import React, { useState } from 'react';
import axios from 'axios';
import AsyncSelect from 'react-select/async';
import '../../../shared/StyledInput.css';
import RequiredMark from '../../../shared/RequiredMark';
import CancelButton from '../../../shared/CancelButton';
import SaveButton from '../../../shared/SaveButton';
import DateInput from '../../../shared/DateInput';

// Matches styled-input's 34px height, same styles object shape as
// AddLinkDrawer.js's own entity picker.
const seriesSelectStyles = {
  container: (base) => ({ ...base, width: 260 }),
  control: (base) => ({
    ...base,
    minHeight: 34,
    height: 34,
    borderRadius: 4,
    borderColor: '#a9a9a9',
    boxShadow: 'none',
    fontSize: 15,
    '&:hover': { borderColor: '#a9a9a9' },
  }),
  valueContainer: (base) => ({ ...base, height: 32, padding: '0 8px' }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  indicatorsContainer: (base) => ({ ...base, height: 32 }),
  indicatorSeparator: () => ({ display: 'none' }),
  option: (base) => ({ ...base, fontSize: 14 }),
};

// Add/Edit Trade Show — Series is picked from an existing
// TradeShowSeries via search, or created inline, same search-or-create
// fallback AddLinkDrawer.js uses for People/Companies.
function TradeShowForm({ initial, onSave, onCancel, loading }) {
  const [creatingNewSeries, setCreatingNewSeries] = useState(false);
  const [seriesOption, setSeriesOption] = useState(
    initial?.series ? { value: initial.trade_show_series_id, label: initial.series.name } : null
  );
  const [newSeriesName, setNewSeriesName] = useState('');
  const [year, setYear] = useState(initial?.year ? String(initial.year) : '');
  const [location, setLocation] = useState(initial?.location || '');
  const [startDate, setStartDate] = useState(initial?.start_date || '');
  const [endDate, setEndDate] = useState(initial?.end_date || '');
  const [website, setWebsite] = useState(initial?.website || '');
  const [error, setError] = useState('');

  const loadSeriesOptions = (inputValue, callback) => {
    if (!inputValue || inputValue.length < 1) return callback([]);
    axios.get('/api/trade-show-series/quick-search', { params: { q: inputValue } })
      .then((res) => callback((res.data.data || []).map((r) => ({ value: r.id, label: r.name }))))
      .catch(() => callback([]));
  };

  const validate = () => {
    if (creatingNewSeries ? !newSeriesName.trim() : !seriesOption) return 'Series is required.';
    if (!year.trim() || isNaN(parseInt(year, 10))) return 'Year is required.';
    if (startDate && endDate && endDate < startDate) return 'End Date can\'t be before Start Date.';
    return '';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const err = validate();
    if (err) return setError(err);
    setError('');
    const payload = { year: parseInt(year, 10), location: location.trim(), start_date: startDate || null, end_date: endDate || null, website: website.trim() };
    if (creatingNewSeries) payload.series_name = newSeriesName.trim();
    else payload.trade_show_series_id = seriesOption.value;
    onSave(payload);
  };

  return (
    <form onSubmit={handleSubmit} style={{ minWidth: 320 }}>
      <div>
        <label>
          Series<RequiredMark />{' '}
          <button
            type="button"
            onClick={() => { setCreatingNewSeries((v) => !v); setSeriesOption(null); setError(''); }}
            style={{ background: 'none', border: 'none', color: '#1976d2', cursor: 'pointer', textDecoration: 'underline', fontSize: 11, padding: 0 }}
          >
            {creatingNewSeries ? 'Cancel' : '+ Add new series'}
          </button>
        </label>
        {creatingNewSeries ? (
          <input
            className="styled-input"
            value={newSeriesName}
            onChange={(e) => setNewSeriesName(e.target.value)}
            maxLength={100}
            placeholder="e.g., Integrate"
            style={{ width: 260 }}
          />
        ) : (
          <AsyncSelect
            cacheOptions
            defaultOptions={false}
            loadOptions={loadSeriesOptions}
            value={seriesOption}
            onChange={setSeriesOption}
            placeholder="Search series..."
            isClearable
            styles={seriesSelectStyles}
            noOptionsMessage={() => 'No series found'}
            loadingMessage={() => 'Searching...'}
            aria-label="Series"
          />
        )}
      </div>

      <div style={{ marginTop: 12 }}>
        <label>Year<RequiredMark /></label><br />
        <input
          className="styled-input"
          type="number"
          value={year}
          onChange={(e) => setYear(e.target.value)}
          placeholder="e.g., 2026"
          style={{ width: 260 }}
        />
      </div>

      <div style={{ marginTop: 12 }}>
        <label>Location</label><br />
        <input
          className="styled-input"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          maxLength={150}
          placeholder="e.g., Sydney"
          style={{ width: 260 }}
        />
      </div>

      <div style={{ marginTop: 12, display: 'flex', gap: 12 }}>
        <div>
          <label>Start Date</label><br />
          <DateInput
            className="styled-input"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            style={{ width: 124 }}
          />
        </div>
        <div>
          <label>End Date</label><br />
          <DateInput
            className="styled-input"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            style={{ width: 124 }}
          />
        </div>
      </div>

      <div style={{ marginTop: 12 }}>
        <label>Website</label><br />
        <input
          className="styled-input"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          maxLength={255}
          placeholder="e.g., integrate.com.au"
          style={{ width: 260 }}
        />
      </div>

      {error && <div style={{ color: 'red', marginTop: 8 }}>{error}</div>}

      <div style={{ marginTop: 12 }}>
        <SaveButton loading={loading} />
        <CancelButton onClick={onCancel} disabled={loading} style={{ marginLeft: 8 }} />
      </div>
    </form>
  );
}

export default TradeShowForm;
